// src/pages/Accounting/Reports/AccountList/components/CustomizeDataTab.tsx

import React from 'react';
import { ChevronDown, ArrowUp, ArrowDown, Trash2 } from 'lucide-react';
import { AVAILABLE_COLUMNS, FILTER_CONDITIONS, FILTER_FIELDS } from '../constants';
import type { ReportCustomization, ReportFilter } from '../types';

interface CustomizeDataTabProps {
  draft: ReportCustomization;
  updateDraft: (patch: Partial<ReportCustomization>) => void;
}

export default function CustomizeDataTab({ draft, updateDraft }: CustomizeDataTabProps) {
  const addFilter = () => {
    updateDraft({ filters: [...draft.filters, { field: 'deleted', condition: 'equals', value: 'No' }] });
  };
  const updateFilter = (i: number, patch: Partial<ReportFilter>) => {
    updateDraft({ filters: draft.filters.map((f, idx) => idx === i ? { ...f, ...patch } : f) });
  };
  const removeFilter = (i: number) => {
    updateDraft({ filters: draft.filters.filter((_, idx) => idx !== i) });
  };
  const clearFilters = () => updateDraft({ filters: [] });

  const toggleColumn = (key: string) => {
    updateDraft({
      columns: draft.columns.map((c) => c.key === key ? { ...c, visible: !c.visible } : c),
    });
  };
  const moveColumn = (key: string, dir: -1 | 1) => {
    const sorted = [...draft.columns].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((c) => c.key === key);
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const tmp = sorted[idx].order;
    sorted[idx].order = sorted[swapIdx].order;
    sorted[swapIdx].order = tmp;
    updateDraft({ columns: sorted });
  };

  return (
    <div>
      {/* Filters */}
      <SectionHeader title="Filters" />
      <div className="flex justify-between items-center text-[11.5px] text-ink3 font-semibold mb-2">
        Select your data filtering method
        {draft.filters.length > 0 && (
          <button type="button" onClick={clearFilters} className="text-teal-dark border-none bg-transparent cursor-pointer hover:brightness-110">
            Clear all
          </button>
        )}
      </div>

      {draft.filters.map((f, i) => (
        <div key={i} className="mb-3 pb-3 border-b border-lineSoft last:border-b-0">
          <FieldLabel>Filter by</FieldLabel>
          <SelectRow value={f.field} onChange={(v) => updateFilter(i, { field: v })}>
            {FILTER_FIELDS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </SelectRow>
          <FieldLabel>Condition</FieldLabel>
          <SelectRow value={f.condition} onChange={(v) => updateFilter(i, { condition: v as ReportFilter['condition'] })}>
            {FILTER_CONDITIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </SelectRow>
          <FieldLabel>Value</FieldLabel>
          <input
            value={f.value}
            onChange={(e) => updateFilter(i, { value: e.target.value })}
            className="w-full bg-white border border-line rounded-md px-2.5 py-1.5 text-[12px] text-ink outline-none focus:border-teal-dark mb-2"
          />
          <button type="button" onClick={() => removeFilter(i)} className="text-[11.5px] text-ink3 inline-flex items-center gap-1 border-none bg-transparent cursor-pointer hover:text-[#B33E1A]">
            <Trash2 size={12} strokeWidth={2} />
            Remove filter
          </button>
        </div>
      ))}

      <button type="button" onClick={addFilter} className="text-[11.5px] text-teal-dark font-semibold border-none bg-transparent cursor-pointer hover:brightness-110">
        + Add filter
      </button>

      {/* Columns */}
      <SectionHeader title="Columns" className="mt-6" />
      <div className="text-[11.5px] text-ink3 font-semibold mb-2">Click to show/hide. Use arrows to reorder.</div>
      {[...draft.columns].sort((a, b) => a.order - b.order).map((c, i, arr) => {
        const def = AVAILABLE_COLUMNS.find((x) => x.key === c.key);
        return (
          <div key={c.key as string} className="flex items-center gap-2 py-1.5 text-[12px] text-ink">
            <button type="button" onClick={() => moveColumn(c.key as string, -1)} disabled={i === 0} className="text-ink4 border-none bg-transparent cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed hover:text-ink">
              <ArrowUp size={13} strokeWidth={2} />
            </button>
            <button type="button" onClick={() => moveColumn(c.key as string, 1)} disabled={i === arr.length - 1} className="text-ink4 border-none bg-transparent cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed hover:text-ink">
              <ArrowDown size={13} strokeWidth={2} />
            </button>
            <button type="button" onClick={() => toggleColumn(c.key as string)} className={`w-3.5 h-3.5 border-[1.5px] rounded-sm inline-flex items-center justify-center text-white text-[10px] cursor-pointer ${
              c.visible ? 'bg-teal-dark border-teal-dark' : 'bg-white border-line'
            }`} aria-label={def?.label}>
              {c.visible && '✓'}
            </button>
            <span>{def?.label ?? c.key}</span>
          </div>
        );
      })}

      {/* Number format */}
      <SectionHeader title="Number format" className="mt-6" />
      <div className="text-[11.5px] text-ink3 font-semibold mb-2">Choose how to format your data</div>
      <div className="flex items-center gap-2 py-1 text-[12px] text-ink">
        <input type="checkbox" checked={draft.numberFormat.divideByThousand} onChange={(e) => updateDraft({ numberFormat: { ...draft.numberFormat, divideByThousand: e.target.checked } })} className="accent-teal-dark" />
        Divide by 1000
      </div>
      <div className="flex items-center gap-2 py-1 text-[12px] text-ink">
        <input type="checkbox" checked={draft.numberFormat.hideCurrencySymbol} onChange={(e) => updateDraft({ numberFormat: { ...draft.numberFormat, hideCurrencySymbol: e.target.checked } })} className="accent-teal-dark" />
        Don't show currency symbol
      </div>
      <FieldLabel className="mt-2">Negative numbers</FieldLabel>
      <SelectRow value={draft.numberFormat.negativeStyle} onChange={(v) => updateDraft({ numberFormat: { ...draft.numberFormat, negativeStyle: v as 'parentheses' | 'minus' } })}>
        <option value="parentheses">(100)</option>
        <option value="minus">-100</option>
      </SelectRow>
    </div>
  );
}

function SectionHeader({ title, className = '' }: { title: string; className?: string }) {
  return <div className={`text-[13px] font-bold text-ink mb-2 flex justify-between items-center ${className}`}>{title}</div>;
}
function FieldLabel({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <label className={`text-[11px] font-semibold text-ink3 block mb-1.5 ${className}`}>{children}</label>;
}
function SelectRow({ value, onChange, children }: { value: string; onChange: (v: string) => void; children: React.ReactNode }) {
  return (
    <div className="relative mb-2">
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-white border border-line rounded-md px-2.5 py-1.5 text-[12px] text-ink outline-none focus:border-teal-dark appearance-none cursor-pointer pr-8">
        {children}
      </select>
      <ChevronDown size={11} strokeWidth={2.5} className="text-ink3 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
    </div>
  );
}